"use client";

import { errorHandler } from "@/info/placeholders";
import { DEFAULT_IMAGE_QUALITY_BLUR, DEFAULT_IMAGE_QUALITY_MAIN, normalizeQuality, type ImageQuality } from "@/lib/constants/images";
import { cn } from "@/utils/cn";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import * as React from "react";
import "./fullscreen-transitions.css";
import { PinchZoomImage } from "./pinch-zoom-image";
export interface ImageGalleryImage {
  url: string;
  alt?: string;
  space?: string;
  name?: string;
  priority?: boolean;
}
export interface ImageGalleryCategory {
  name: string;
  code: string;
}
export interface ImageGalleryProps {
  images: ImageGalleryImage[];
  categories?: ImageGalleryCategory[];
  className?: string;
  showNavigationButtons?: boolean;
  showImageCounter?: boolean;
  enableKeyboardNavigation?: boolean;
  enableDoubleTapFullscreen?: boolean;
  autoLoop?: boolean;
  children?: React.ReactNode; // For overlays like dots, categories, etc.
  // Image optimization props
  sizes?: string;
  quality?: ImageQuality | number;
  onMainImageClick?: () => void;
}
interface ImageGalleryContextType {
  currentIndex: number;
  images: ImageGalleryImage[];
  goToIndex: (index: number) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  toggleFullscreen: () => void;
  isFullscreen: boolean;
  emblaApi: ReturnType<typeof useEmblaCarousel>[1];
}
const ImageGalleryContext = React.createContext<ImageGalleryContextType | null>(null);
export function ImageGallery({
  images,
  categories: _categories = [],
  className,
  showNavigationButtons = true,
  showImageCounter = true,
  enableKeyboardNavigation = true,
  enableDoubleTapFullscreen = true,
  autoLoop = true,
  children,
  // Image optimization props
  sizes,
  quality,
  onMainImageClick
}: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  // Use ref instead of state for hover to prevent re-renders, as it's only used for logic guards
  const isHoveredRef = React.useRef(false);

  // Hover handlers with useCallback to prevent re-renders
  const handleMouseEnter = React.useCallback(() => {
    isHoveredRef.current = true;
  }, []);
  const handleMouseLeave = React.useCallback(() => {
    isHoveredRef.current = false;
  }, []);

  // Track initial touch position to determine drag direction
  const touchStartRef = React.useRef<{
    x: number;
    y: number;
  } | null>(null);

  // Embla carousel with axis lock to allow vertical scrolling to pass through
  const [emblaRef, emblaApi_0] = useEmblaCarousel({
    loop: autoLoop,
    axis: "x",
    // Lock to horizontal axis only
    dragFree: false,
    // Increase threshold to prevent accidental horizontal drags during vertical scrolling
    dragThreshold: 10,
    // Allow vertical scrolling to pass through to parent (for closing overlay)
    watchDrag: (emblaApi, event) => {
      const target = event.target as HTMLElement;

      // Don't interfere with pinch gestures (multi-touch) - important for zoom
      const touchEvent = event as TouchEvent;
      if (touchEvent.touches && touchEvent.touches.length > 1) {
        touchStartRef.current = null;
        return false;
      }

      // Track touch start position
      if (touchEvent.type === "touchstart" && touchEvent.touches && touchEvent.touches[0]) {
        touchStartRef.current = {
          x: touchEvent.touches[0].clientX,
          y: touchEvent.touches[0].clientY
        };
      }

      // Detect drag direction on touchmove
      if (touchEvent.type === "touchmove" && touchEvent.touches && touchEvent.touches[0] && touchStartRef.current) {
        const deltaX = Math.abs(touchEvent.touches[0].clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touchEvent.touches[0].clientY - touchStartRef.current.y);

        // If vertical movement is greater than horizontal, don't let Embla handle it
        if (deltaY > deltaX && deltaY > 5) {
          touchStartRef.current = null;
          return false;
        }
      }

      // Allow drag on images and carousel container
      return !target.closest("button") && !target.closest('a:not([role="button"])');
    }
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Double tap state for fullscreen
  const lastTapRef = React.useRef<number>(0);
  const tapCountRef = React.useRef<number>(0);
  const clickTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  // Navigation functions
  const goToPrevious = React.useCallback(() => {
    emblaApi_0?.scrollPrev();
  }, [emblaApi_0]);
  const goToNext = React.useCallback(() => {
    emblaApi_0?.scrollNext();
  }, [emblaApi_0]);
  const goToIndex = React.useCallback((index: number) => {
    if (index >= 0 && index < images.length) {
      emblaApi_0?.scrollTo(index);
    }
  }, [images.length, emblaApi_0]);

  // Check if fullscreen API is supported
  const isFullscreenSupported = React.useMemo(() => {
    if (typeof document === "undefined") return false;
    return !!(document.fullscreenEnabled ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document as any).webkitFullscreenEnabled ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document as any).mozFullScreenEnabled ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document as any).msFullscreenEnabled);
  }, []);

  // Fullscreen functionality with smooth transitions
  const toggleFullscreen = React.useCallback(() => {
    if (!containerRef.current || isTransitioning) return;

    // If fullscreen API is not supported (iOS Safari, some mobile browsers),
    // toggle a simulated fullscreen using CSS/state
    if (!isFullscreenSupported) {
      setIsTransitioning(true);
      setIsFullscreen(prev => !prev);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      return;
    }
    setIsTransitioning(true);
    const container = containerRef.current;

    // Add pre-transition class for smooth scaling
    container.classList.add("transitioning-fullscreen");

    // Helper to finish transition
    const finishTransition = () => {
      setTimeout(() => {
        setIsTransitioning(false);
        container.classList.remove("transitioning-fullscreen");
      }, 300); // Match CSS transition duration
    };

    // Helper to abort transition
    const abortTransition = () => {
      setIsTransitioning(false);
      container.classList.remove("transitioning-fullscreen");
    };

    // @ts-ignore
    const isFullscreenActive = document.fullscreenElement ||
    // @ts-ignore
    document.webkitFullscreenElement ||
    // @ts-ignore
    document.mozFullScreenElement ||
    // @ts-ignore
    document.msFullscreenElement;
    if (isFullscreenActive) {
      // Exit fullscreen with smooth transition
      const exitFunc = document.exitFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).webkitExitFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).mozCancelFullScreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).msExitFullscreen;
      if (typeof exitFunc === "function") {
        const p = exitFunc.call(document);
        if (p && typeof p.then === "function") {
          p.then(finishTransition).catch(abortTransition);
        } else {
          finishTransition();
        }
      } else {
        abortTransition();
      }
    } else {
      // Enter fullscreen with smooth transition
      const requestFunc = container.requestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).webkitRequestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).mozRequestFullScreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).msRequestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).webkitEnterFullscreen; // Video specific, but for completeness

      if (typeof requestFunc === "function") {
        try {
          const p_0 = requestFunc.call(container);
          if (p_0 && typeof p_0.then === "function") {
            p_0.then(finishTransition).catch(abortTransition);
          } else {
            finishTransition();
          }
        } catch (_e) {
          abortTransition();
        }
      } else {
        abortTransition();
      }
    }
  }, [isTransitioning, isFullscreenSupported]);

  // Double tap handler for fullscreen - FULLY RESTORED
  const handleDoubleTap = React.useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Always prevent default behavior and bubbling to ensure we control the click
    e.preventDefault();
    e.stopPropagation();
    if ("nativeEvent" in e) {
      e.nativeEvent.stopImmediatePropagation();
    }

    // Clear any pending single click
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = undefined;
    }
    if (!enableDoubleTapFullscreen) {
      // If double tap disabled, just treat as single click immediately
      onMainImageClick?.();
      return;
    }
    const now = Date.now();
    const timeDiff = now - lastTapRef.current;
    if (timeDiff < 300 && timeDiff > 0) {
      // Double tap detected
      toggleFullscreen();
      tapCountRef.current = 0;
    } else {
      // Potential single tap
      tapCountRef.current = 1;
      // Delay single click action to check for double tap
      clickTimeoutRef.current = setTimeout(() => {
        onMainImageClick?.();
      }, 300);
    }
    lastTapRef.current = now;
  }, [enableDoubleTapFullscreen, toggleFullscreen, onMainImageClick]);

  // Keyboard navigation
  const handleKeyDown = React.useCallback((event_0: KeyboardEvent) => {
    if (!enableKeyboardNavigation) return;

    // Only handle keyboard navigation if gallery is hovered or focused, or in fullscreen
    if (!isHoveredRef.current && !isFullscreen) return;
    switch (event_0.key) {
      case "ArrowLeft":
        event_0.preventDefault();
        event_0.stopPropagation();
        goToPrevious();
        break;
      case "ArrowRight":
        event_0.preventDefault();
        event_0.stopPropagation();
        goToNext();
        break;
    }
  }, [enableKeyboardNavigation, goToPrevious, goToNext, isFullscreen]);

  // Embla carousel event handlers
  React.useEffect(() => {
    if (!emblaApi_0) return;
    const onSelect = () => {
      setCurrentIndex(emblaApi_0.selectedScrollSnap());
    };
    emblaApi_0.on("select", onSelect);
    onSelect(); // Set initial index

    return () => {
      emblaApi_0.off("select", onSelect);
    };
  }, [emblaApi_0]);

  // Handle fullscreen change event with smooth transitions
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;

      // Add a small delay to allow browser to complete fullscreen transition
      requestAnimationFrame(() => {
        setIsFullscreen(isNowFullscreen);
      });
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Keyboard event listener
  React.useEffect(() => {
    // Use capture phase to handle the event before it can bubble
    document.addEventListener("keydown", handleKeyDown, {
      capture: true
    });
    return () => document.removeEventListener("keydown", handleKeyDown, {
      capture: true
    });
  }, [handleKeyDown]);

  // Current image
  const currentImage = images[currentIndex];

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    currentIndex,
    images,
    goToIndex,
    goToPrevious,
    goToNext,
    toggleFullscreen,
    isFullscreen,
    emblaApi: emblaApi_0
  }), [currentIndex, images, goToIndex, goToPrevious, goToNext, toggleFullscreen, isFullscreen, emblaApi_0]);
  if (!images || images.length === 0) {
    return <div className="flex items-center justify-center w-full h-96 bg-gray-100">
				<div className="text-center">
					<div className="w-16 h-16 bg-gray-300 mx-auto mb-4" />
					<p className="text-gray-500">No images available</p>
				</div>
			</div>;
  }
  return <div ref={containerRef} className={cn("relative w-full h-full bg-white overflow-hidden focus:outline-none",
  // Simulated fullscreen for browsers that don't support fullscreen API (iOS Safari)
  isFullscreen && !isFullscreenSupported && "fixed inset-0 z-[9999] w-screen h-screen", className)} tabIndex={0} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out",
    willChange: "transform, opacity"
  }}>
			{/* Main Image */}
			<div className="relative w-full h-full overflow-hidden">
				{/* Background blur effect - low priority since it's decorative */}
				{currentImage?.url && <div className="absolute inset-[-2rem] overflow-hidden transition-all duration-300 ease-out" style={{
        opacity: isFullscreen ? 0.1 : 0.3,
        transform: isTransitioning ? "scale(1.05)" : "scale(1)"
      }}>
						<Image src={currentImage.url} alt="" fill className="object-cover transition-all duration-300 ease-out" style={{
          filter: isFullscreen ? "blur(2rem)" : "blur(1rem)"
        }}
        // Prioritize first image background blur for LCP optimization
        priority={currentImage?.priority} fetchPriority={currentImage?.priority ? "high" : "low"} loading={currentImage?.priority ? "eager" : "lazy"} sizes={sizes || "100vw"} quality={normalizeQuality(quality, DEFAULT_IMAGE_QUALITY_BLUR)} onError={errorHandler} />
					</div>}

				{/* Embla Carousel */}
				<div ref={emblaRef} className="h-full" style={{
        // In fullscreen, allow pinch-zoom. Outside fullscreen, allow vertical panning for overlay close
        // pan-y tells browser to handle vertical scrolling (important for overlay close)
        // manipulation removes default double-tap zoom delay
        touchAction: isFullscreen ? "manipulation pinch-zoom" : "pan-y manipulation"
      }}>
					<div className="flex h-full">
						{images.map((image, index_0) => {
            // Load eagerly: first image (LCP), current image, and adjacent images
            const isFirstImage = index_0 === 0;
            const isNearCurrentImage = Math.abs(index_0 - currentIndex) <= 1;
            const shouldLoadEagerly = isFirstImage || isNearCurrentImage;
            const isPriorityImage = image.priority === true || isFirstImage;
            const isFloorPlan = image.space === "floorplan";
            const shouldEnableZoom = isFullscreen && isFloorPlan;

            // Performance Optimization: Lazy mounting
            // Only mount content for current slide and adjacent slides (buffer)
            // This significantly reduces DOM nodes and memory usage for large galleries
            // especially when multiple galleries are rendered on a page (e.g. lists)
            let shouldRenderContent = isNearCurrentImage;
            if (autoLoop && images.length > 1) {
              const lastIndex = images.length - 1;
              // Handle wrap-around for loop mode
              if (currentIndex === 0 && index_0 === lastIndex) shouldRenderContent = true;
              if (currentIndex === lastIndex && index_0 === 0) shouldRenderContent = true;
            }
            return <div key={index_0} className="flex-[0_0_100%] min-w-0 relative cursor-pointer flex items-center justify-center" onClick={shouldEnableZoom ? undefined : handleDoubleTap} style={{
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out",
              transform: isTransitioning ? "scale(0.95)" : "scale(1)",
              opacity: isTransitioning && isFullscreenSupported ? 0.95 : 1
            }}>
									{shouldRenderContent ? image?.url ? shouldEnableZoom ? <PinchZoomImage src={image.url} alt={image.alt || `Image ${index_0 + 1}`} sizes={sizes || "100vw"} quality={normalizeQuality(quality, DEFAULT_IMAGE_QUALITY_MAIN)} priority={isPriorityImage} fetchPriority={isPriorityImage ? "high" : "low"} loading={shouldLoadEagerly ? "eager" : "lazy"} onError={errorHandler} /> : <Image src={image.url} alt={image.alt || `Image ${index_0 + 1}`} fill className="object-contain transition-all duration-300 ease-out" sizes={sizes || "100vw"} quality={normalizeQuality(quality, DEFAULT_IMAGE_QUALITY_MAIN)} priority={isPriorityImage} fetchPriority={isPriorityImage ? "high" : "low"} loading={shouldLoadEagerly ? "eager" : "lazy"} onError={errorHandler} /> : <div className="w-full h-full bg-gray-200 flex items-center justify-center">
												<span className="text-gray-500 text-sm">No image available</span>
											</div> : null}
								</div>;
          })}
					</div>
				</div>
			</div>

			{/* Image Counter */}
			{showImageCounter && images.length > 1 && <div className="absolute top-3 left-3 z-10 flex items-center h-6">
					<div className="bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
						{currentIndex + 1} / {images.length}
						{currentImage.name && ` • ${currentImage.name}`}
					</div>
				</div>}

			{/* Navigation Areas - Hidden on mobile to avoid interfering with swipe gestures */}
			{showNavigationButtons && images.length > 1 && <>
					{/* Left Navigation Area */}
					<div role="button" tabIndex={-1} onKeyDown={e_0 => {
        if (e_0.key === "Enter" || e_0.key === " ") {
          e_0.preventDefault();
          e_0.stopPropagation();
          goToPrevious();
        }
      }} onMouseDown={e_1 => {
        // Prevent potential link dragging or focus events from bubbling
        e_1.preventDefault();
        e_1.stopPropagation();
      }} onClick={e_2 => {
        e_2.preventDefault();
        e_2.stopPropagation();
        e_2.nativeEvent.stopImmediatePropagation();

        // Single tap - navigate
        goToPrevious();
      }} className="hidden md:block absolute left-0 top-0 bottom-0 w-1/4 min-w-[44px] min-h-[44px] z-10 bg-transparent hover:bg-black/10 transition-colors duration-200 cursor-pointer outline-none" aria-label="Previous image" />

					{/* Right Navigation Area */}
					<div role="button" tabIndex={-1} onKeyDown={e_3 => {
        if (e_3.key === "Enter" || e_3.key === " ") {
          e_3.preventDefault();
          e_3.stopPropagation();
          goToNext();
        }
      }} onMouseDown={e_4 => {
        // Prevent potential link dragging or focus events from bubbling
        e_4.preventDefault();
        e_4.stopPropagation();
      }} onClick={e_5 => {
        e_5.preventDefault();
        e_5.stopPropagation();
        e_5.nativeEvent.stopImmediatePropagation();

        // Single tap - navigate
        goToNext();
      }} className="hidden md:block absolute right-0 top-0 bottom-0 w-1/4 min-w-[44px] min-h-[44px] z-10 bg-transparent hover:bg-black/10 transition-colors duration-200 cursor-pointer outline-none" aria-label="Next image" />
				</>}

			{/* Custom overlays (dots, categories, etc.) */}
			<ImageGalleryContext.Provider value={contextValue}>{children}</ImageGalleryContext.Provider>
		</div>;
}

// Hook to provide gallery control functions to children
export function useImageGallery() {
  const context = React.useContext(ImageGalleryContext);
  if (!context) {
    throw new Error("useImageGallery must be used within an ImageGallery component");
  }
  return context;
}